import React, { useRef, useState } from "react";
import { Upload, X, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fileSizeLabel } from "../lib/constants";

const inputCls =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--atech-copper)]";

/* ─── Status indicator ─── */
function StatusIcon({ status }) {
  if (status === "uploading") return (
    <div className="w-4 h-4 rounded-full border-2 border-[color:var(--atech-copper)] border-t-transparent animate-spin shrink-0" />
  );
  if (status === "done") return <CheckCircle2 size={16} className="text-[#34D399] shrink-0" />;
  if (status === "error") return <AlertCircle size={16} className="text-[#F87171] shrink-0" />;
  return <div className="w-4 h-4 rounded-full border border-white/20 shrink-0" />;
}

/* ─── Modal shell ─── */
function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)] rounded-[2px] shadow-2xl">
        <div className="flex items-center justify-between px-8 py-5 border-b border-[color:var(--atech-border)]">
          <h2 className="font-display text-xl text-[color:var(--atech-text)]">{title}</h2>
          {onClose && (
            <button onClick={onClose} className="text-[color:var(--atech-text-muted)] hover:text-white transition-colors">
              <X size={18} />
            </button>
          )}
        </div>
        <div className="px-8 py-6">{children}</div>
      </div>
    </div>
  );
}

export default function MassUploadModal({ projectId, folders, currentFolder, onClose, onUploaded }) {
  const [fileItems, setFileItems] = useState([]);
  const [targetFolder, setTargetFolder] = useState(currentFolder || "");
  const [visible, setVisible] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const items = Array.from(fileList).map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      title: f.name.replace(/\.[^.]+$/, ""),
      status: "pending",
      error: "",
    }));
    setFileItems((prev) => [...prev, ...items]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id) => {
    setFileItems((prev) => prev.filter((item) => item.id !== id));
  };

  const uploadOne = async (item) => {
    setFileItems((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "uploading" } : f));
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("title", item.title);
    fd.append("document_status", "issued_for_information");
    fd.append("visible_to_client", String(visible));
    if (targetFolder) fd.append("folder_id", targetFolder);
    try {
      await api.post(`/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileItems((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "done" } : f));
    } catch (e) {
      setFileItems((prev) => prev.map((f) =>
        f.id === item.id ? { ...f, status: "error", error: formatApiErrorDetail(e.response?.data?.detail) || e.message } : f
      ));
    }
  };

  const uploadAll = async () => {
    setUploading(true);
    const items = [...fileItems]; // snapshot
    const CONCURRENT = 3;
    for (let i = 0; i < items.length; i += CONCURRENT) {
      const batch = items.slice(i, i + CONCURRENT);
      await Promise.all(batch.map((item) => uploadOne(item)));
    }
    setUploading(false);
    setDone(true);
  };

  const total = fileItems.length;
  const doneCount = fileItems.filter((f) => f.status === "done").length;
  const errorCount = fileItems.filter((f) => f.status === "error").length;

  return (
    <Modal onClose={!uploading ? onClose : undefined} title="Upload documents">
      {/* Drag-drop zone */}
      {!uploading && !done && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-[2px] p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? "border-[color:var(--atech-copper)] bg-[rgba(198,132,88,0.05)]"
                : "border-white/10 hover:border-white/25 hover:bg-white/[0.02]"
            }`}
            data-testid="mass-upload-drop-zone"
          >
            <Upload size={28} className="mx-auto mb-3 text-[color:var(--atech-text-muted)]" />
            <div className="text-sm text-[color:var(--atech-text)] font-medium">
              Drop files here or <span className="text-[color:var(--atech-copper)]">browse</span>
            </div>
            <div className="text-xs text-[color:var(--atech-text-muted)] mt-1.5">
              Select as many files as you need — they all upload in one go
            </div>
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={(e) => addFiles(e.target.files)}
              className="hidden"
              data-testid="mass-upload-file-input"
            />
          </div>

          {total > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <label className="block">
                <span className="label block mb-2">Folder (applies to all)</span>
                <select
                  value={targetFolder}
                  onChange={(e) => setTargetFolder(e.target.value)}
                  className={inputCls}
                  data-testid="mass-upload-folder-select"
                >
                  <option value="">Unfiled</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-3 self-end pb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => setVisible(e.target.checked)}
                  className="accent-[color:var(--atech-copper)]"
                  data-testid="mass-upload-visible-toggle"
                />
                <span className="text-sm text-[color:var(--atech-text-dim)]">Visible to client</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* File queue */}
      {total > 0 && (
        <ul className="mt-4 space-y-1 max-h-64 overflow-y-auto" data-testid="mass-upload-queue">
          {fileItems.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-[2px] bg-[color:var(--atech-surface-elevated)] border border-white/5"
              data-testid={`mass-upload-item-${item.id}`}
            >
              <StatusIcon status={item.status} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[color:var(--atech-text)] truncate">{item.title}</div>
                {item.error
                  ? <div className="text-[11px] text-[#F87171]">{item.error}</div>
                  : <div className="text-[11px] text-[color:var(--atech-text-muted)]">{fileSizeLabel(item.file.size)}</div>
                }
              </div>
              {!uploading && item.status === "pending" && (
                <button
                  onClick={() => removeItem(item.id)}
                  className="text-[color:var(--atech-text-muted)] hover:text-[#F87171] transition-colors"
                  data-testid={`mass-upload-remove-${item.id}`}
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-[color:var(--atech-border)]">
        <div className="text-xs text-[color:var(--atech-text-muted)]">
          {done
            ? `${doneCount} uploaded${errorCount > 0 ? `, ${errorCount} failed` : ""}`
            : uploading
              ? `Uploading… ${doneCount + errorCount} of ${total}`
              : total === 0
                ? "No files selected"
                : `${total} file${total === 1 ? "" : "s"} ready`}
        </div>
        <div className="flex gap-3">
          {!uploading && !done && (
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"
            >
              Cancel
            </button>
          )}
          {!done ? (
            <button
              type="button"
              onClick={uploadAll}
              disabled={uploading || total === 0}
              className="btn-copper text-sm disabled:opacity-40"
              data-testid="mass-upload-submit"
            >
              {uploading ? "Uploading…" : `Upload ${total > 0 ? total : ""} file${total === 1 ? "" : "s"}`}
            </button>
          ) : (
            <button
              type="button"
              onClick={onUploaded}
              className="btn-copper text-sm flex items-center gap-2"
              data-testid="mass-upload-done"
            >
              <CheckCircle2 size={14} /> Done
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
